/**
 * Conception manuelle des emplois du temps d'examens (2026-09-14, complétée le 2026-09-15).
 *
 * Grille vide (jours × horaires × colonnes d'options) que la scolarité remplit peu à peu :
 * on choisit un groupe de matières dans la liste des matières à placer, puis on le dépose sur
 * un créneau (clic ou glisser-déposer). Conception par niveau (tous pôles) ou par niveau et
 * pôle. Les TP et les matières inactives ne sont jamais proposés. Les noms d'une même matière
 * saisis différemment d'une option à l'autre sont détectés et peuvent être fusionnés pour l'EDT.
 */
import React, { useMemo, useState } from 'react';
import { AlertTriangle, Loader2, Search, X } from 'lucide-react';
import {
  useGetMatieresAPlacerQuery,
  usePreparerGrilleMutation,
  useGetNiveauxDisponiblesQuery,
  useGetEmploiTempsGrilleManuelleQuery,
  usePlacerMatiereMutation,
  useRetirerMatiereMutation,
  useDeplacerMatiereMutation,
  useGetDoublonsMatieresQuery,
  useModifierCorrespondanceMatieresMutation,
  SessionExamen,
  GroupeMatiereAPlacer,
  AvertissementConception,
  ConflitConception,
  SuggestionDoublon,
} from '../../services/emploisTempsApi';

interface Props {
  session: SessionExamen;
  isDark: boolean;
}

interface Cible {
  niveau: string;
  date: string;
  dateLabel: string;
  heureDebut: string;
  heureFin: string;
}

interface CreneauPose {
  niveau: string;
  nom: string;
  date: string;
  dateLabel: string;
  heureDebut: string;
  heureFin: string;
  groupeId: number;
  groupeLabel: string;
}

type Modale =
  | { type: 'placer'; item: GroupeMatiereAPlacer; cible: Cible }
  | { type: 'deplacer'; source: CreneauPose; cible: Cible }
  | { type: 'actions'; source: CreneauPose }
  | { type: 'doublons' }
  | null;

const DND = 'application/x-edt-manuel';
const NIVEAUX = ['L1', 'L2', 'L3'];
const POLES: Record<string, string> = { polytechnique: 'Polytechnique', commerce_juridique: 'Commerce & Juridique' };
const CONFIANCE: Record<string, string> = { certain: 'Quasi certain', probable: 'Probable', possible: 'À vérifier' };

const libellePole = (p: string) => POLES[p] || (p ? p.charAt(0).toUpperCase() + p.slice(1) : '—');
const minutes = (hm: string) => {
  const [h, m] = (hm || '00:00').split(':').map((x) => parseInt(x, 10) || 0);
  return h * 60 + m;
};
const versHeure = (total: number) => {
  const t = Math.max(0, Math.min(total, 23 * 60 + 59));
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};
// « 10H » / « 10H30 » → « 10:00 » / « 10:30 »
const labelVersHeure = (label?: string | null) => {
  const m = /^(\d{1,2})H(\d{2})?$/.exec((label || '').trim());
  return m ? `${m[1].padStart(2, '0')}:${m[2] || '00'}` : '';
};
const dateCourte = (iso: string | null) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' }) : '');

const classesChamp = (isDark: boolean) =>
  `px-2 py-1 rounded border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`;
const boutonPrincipal = 'px-3 py-1.5 rounded text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50';
const boutonSecondaire = (isDark: boolean) =>
  `px-3 py-1.5 rounded text-sm ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} disabled:opacity-50`;
const puce = (isDark: boolean) =>
  `inline-block text-[10px] px-1.5 py-0.5 rounded border ${isDark ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-600'}`;

// ─── Cadre de fenêtre ────────────────────────────────────────────────────────

const Cadre: React.FC<{
  titre: string; isDark: boolean; onFermer: () => void; children: React.ReactNode; pied?: React.ReactNode; large?: boolean;
}> = ({ titre, isDark, onFermer, children, pied, large }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onFermer}>
    <div
      className={`w-full ${large ? 'max-w-3xl' : 'max-w-lg'} rounded-lg shadow-xl max-h-[90vh] flex flex-col ${isDark ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={`px-5 py-3 border-b flex items-center justify-between ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <h3 className="font-semibold text-sm">{titre}</h3>
        <button onClick={onFermer} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
      </div>
      <div className="p-5 overflow-y-auto space-y-3 text-sm">{children}</div>
      {pied && <div className={`px-5 py-3 border-t flex justify-end gap-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>{pied}</div>}
    </div>
  </div>
);

const BlocRetour: React.FC<{ erreur: string; conflits: ConflitConception[]; avertissements: AvertissementConception[] | null }> = ({
  erreur, conflits, avertissements,
}) => (
  <>
    {erreur && (
      <div className="rounded border border-red-300 bg-red-50 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300 p-3 text-xs space-y-1">
        <div className="font-semibold">{erreur}</div>
        {conflits.map((c, i) => (
          <div key={i}>{c.colonne} : « {c.matiere} » ({c.horaire})</div>
        ))}
      </div>
    )}
    {avertissements && avertissements.length > 0 && (
      <div className="rounded border border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200 p-3 text-xs space-y-1">
        <div className="font-semibold flex items-center gap-1"><AlertTriangle size={13} /> À confirmer</div>
        {avertissements.map((a, i) => <div key={i}>{a.message}</div>)}
      </div>
    )}
  </>
);

// ─── Fenêtre : placer un groupe de matières ─────────────────────────────────

const ModalePlacer: React.FC<{
  sessionId: number; pole?: string; item: GroupeMatiereAPlacer; cible: Cible; isDark: boolean;
  onFermer: () => void; onFait: (texte: string) => void;
}> = ({ sessionId, pole, item, cible, isDark, onFermer, onFait }) => {
  const [placer, { isLoading }] = usePlacerMatiereMutation();
  const aPlacer = item.colonnes.filter((c) => !c.place);
  const [choisies, setChoisies] = useState<Set<number>>(new Set(aPlacer.map((c) => c.groupe_id)));
  const [hd, setHd] = useState(cible.heureDebut);
  const [hf, setHf] = useState(cible.heureFin || versHeure(minutes(cible.heureDebut) + 120));
  const [avertissements, setAvertissements] = useState<AvertissementConception[] | null>(null);
  const [erreur, setErreur] = useState('');
  const [conflits, setConflits] = useState<ConflitConception[]>([]);

  const basculer = (id: number) => {
    setAvertissements(null);
    setChoisies((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const envoyer = async (confirmer: boolean) => {
    setErreur('');
    setConflits([]);
    try {
      const r = await placer({
        sessionId, pole, niveau: item.niveau, cle: item.cle, date: cible.date,
        heure_debut: hd, heure_fin: hf, groupe_ids: Array.from(choisies), confirmer,
      }).unwrap();
      if (r.a_confirmer) {
        setAvertissements(r.avertissements || []);
        return;
      }
      onFait(`« ${item.nom} » placée le ${cible.dateLabel} à ${hd} (${r.nb_creneaux} colonne${(r.nb_creneaux || 0) > 1 ? 's' : ''}).`);
    } catch (e: any) {
      setAvertissements(null);
      setErreur(e?.data?.error || 'Placement impossible.');
      setConflits(e?.data?.conflits || []);
    }
  };

  return (
    <Cadre
      titre={`Placer « ${item.nom} » — ${item.niveau}${pole ? ` · ${libellePole(pole)}` : ''}`}
      isDark={isDark}
      onFermer={onFermer}
      pied={(
        <>
          <button onClick={onFermer} className={boutonSecondaire(isDark)}>Annuler</button>
          {avertissements && avertissements.length > 0 ? (
            <button onClick={() => envoyer(true)} disabled={isLoading} className={boutonPrincipal}>
              {isLoading ? 'Placement…' : 'Placer quand même'}
            </button>
          ) : (
            <button onClick={() => envoyer(false)} disabled={isLoading || choisies.size === 0 || minutes(hf) <= minutes(hd)} className={boutonPrincipal}>
              {isLoading ? 'Placement…' : 'Placer'}
            </button>
          )}
        </>
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{cible.dateLabel}</span>
        <input type="time" value={hd} onChange={(e) => { setHd(e.target.value); setAvertissements(null); }} className={classesChamp(isDark)} />
        <span>à</span>
        <input type="time" value={hf} onChange={(e) => { setHf(e.target.value); setAvertissements(null); }} className={classesChamp(isDark)} />
      </div>
      {item.variantes.length > 0 && (
        <div className="text-xs text-gray-500">Noms regroupés : {item.variantes.join(' / ')}</div>
      )}
      <div>
        <div className="text-xs font-medium mb-1 text-gray-500">Colonnes concernées</div>
        <div className="space-y-1">
          {item.colonnes.map((c) => (
            <label key={c.groupe_id} className={`flex items-start gap-2 text-xs ${c.place ? 'text-gray-400' : ''}`}>
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!c.place && choisies.has(c.groupe_id)}
                disabled={c.place}
                onChange={() => basculer(c.groupe_id)}
              />
              <span>
                <span className="font-medium">{c.label}</span>
                <span className="text-gray-500"> · {libellePole(c.pole_key)}</span>
                {item.variantes.length > 0 && <span className="text-gray-500"> · « {c.nom} »</span>}
                {c.place && <span> · déjà placée {dateCourte(c.date)} {c.heure_debut}</span>}
              </span>
            </label>
          ))}
        </div>
        {item.options_sans_colonne.length > 0 && (
          <div className="mt-2 text-xs text-amber-700 dark:text-amber-300">
            Options sans colonne dans la grille : {item.options_sans_colonne.join(', ')}. Mettez à jour les colonnes.
          </div>
        )}
      </div>
      <BlocRetour erreur={erreur} conflits={conflits} avertissements={avertissements} />
    </Cadre>
  );
};

// ─── Fenêtre : déplacer une matière placée ──────────────────────────────────

const ModaleDeplacer: React.FC<{
  sessionId: number; pole?: string; source: CreneauPose; cible: Cible; isDark: boolean;
  onFermer: () => void; onFait: (texte: string) => void;
}> = ({ sessionId, pole, source, cible, isDark, onFermer, onFait }) => {
  const [deplacer, { isLoading }] = useDeplacerMatiereMutation();
  const duree = Math.max(30, minutes(source.heureFin) - minutes(source.heureDebut)) || 120;
  const [hd, setHd] = useState(cible.heureDebut);
  const [hf, setHf] = useState(versHeure(minutes(cible.heureDebut) + duree));
  const [seulementColonne, setSeulementColonne] = useState(false);
  const [avertissements, setAvertissements] = useState<AvertissementConception[] | null>(null);
  const [erreur, setErreur] = useState('');
  const [conflits, setConflits] = useState<ConflitConception[]>([]);

  const envoyer = async (confirmer: boolean) => {
    setErreur('');
    setConflits([]);
    try {
      const r = await deplacer({
        sessionId, pole, niveau: source.niveau, cle: source.nom, date: source.date, heure_debut: source.heureDebut,
        vers_date: cible.date, vers_heure_debut: hd, vers_heure_fin: hf,
        groupe_ids: seulementColonne ? [source.groupeId] : undefined, confirmer,
      }).unwrap();
      if (r.a_confirmer) {
        setAvertissements(r.avertissements || []);
        return;
      }
      onFait(`« ${source.nom} » déplacée au ${cible.dateLabel} à ${hd}.`);
    } catch (e: any) {
      setAvertissements(null);
      setErreur(e?.data?.error || 'Déplacement impossible.');
      setConflits(e?.data?.conflits || []);
    }
  };

  return (
    <Cadre
      titre={`Déplacer « ${source.nom} » — ${source.niveau}`}
      isDark={isDark}
      onFermer={onFermer}
      pied={(
        <>
          <button onClick={onFermer} className={boutonSecondaire(isDark)}>Annuler</button>
          {avertissements && avertissements.length > 0 ? (
            <button onClick={() => envoyer(true)} disabled={isLoading} className={boutonPrincipal}>
              {isLoading ? 'Déplacement…' : 'Déplacer quand même'}
            </button>
          ) : (
            <button onClick={() => envoyer(false)} disabled={isLoading || minutes(hf) <= minutes(hd)} className={boutonPrincipal}>
              {isLoading ? 'Déplacement…' : 'Déplacer'}
            </button>
          )}
        </>
      )}
    >
      <div className="text-xs text-gray-500">
        Actuellement : {source.dateLabel}, {source.heureDebut}-{source.heureFin}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">Vers {cible.dateLabel}</span>
        <input type="time" value={hd} onChange={(e) => { setHd(e.target.value); setAvertissements(null); }} className={classesChamp(isDark)} />
        <span>à</span>
        <input type="time" value={hf} onChange={(e) => { setHf(e.target.value); setAvertissements(null); }} className={classesChamp(isDark)} />
      </div>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={seulementColonne} onChange={(e) => { setSeulementColonne(e.target.checked); setAvertissements(null); }} />
        Déplacer seulement la colonne {source.groupeLabel}
        {pole ? ` (sinon toutes les colonnes ${libellePole(pole)} de ce créneau)` : ' (sinon toutes les colonnes de ce créneau)'}
      </label>
      <BlocRetour erreur={erreur} conflits={conflits} avertissements={avertissements} />
    </Cadre>
  );
};

// ─── Fenêtre : actions sur un créneau placé ─────────────────────────────────

const ModaleActions: React.FC<{
  sessionId: number; pole?: string; source: CreneauPose; isDark: boolean;
  onFermer: () => void; onDeplacer: () => void; onFait: (texte: string) => void;
}> = ({ sessionId, pole, source, isDark, onFermer, onDeplacer, onFait }) => {
  const [retirer, { isLoading }] = useRetirerMatiereMutation();
  const [erreur, setErreur] = useState('');
  const portee = pole ? `toutes les colonnes ${libellePole(pole)} de ce créneau` : 'toutes les colonnes de ce créneau';

  const envoyer = async (seulementColonne: boolean) => {
    const cible = seulementColonne ? `la colonne ${source.groupeLabel}` : portee;
    if (!window.confirm(`Retirer « ${source.nom} » de ${cible} ?\n\nElle reviendra dans la liste des matières à placer.`)) return;
    setErreur('');
    try {
      const r = await retirer({
        sessionId, pole, niveau: source.niveau, cle: source.nom, date: source.date, heure_debut: source.heureDebut,
        groupe_ids: seulementColonne ? [source.groupeId] : undefined,
      }).unwrap();
      onFait(`« ${source.nom} » retirée (${r.nb_retires} créneau${(r.nb_retires || 0) > 1 ? 'x' : ''})`
        + (r.nb_verrouilles ? `, ${r.nb_verrouilles} verrouillé(s) conservé(s).` : '.'));
    } catch (e: any) {
      setErreur(e?.data?.error || 'Retrait impossible.');
    }
  };

  return (
    <Cadre titre={`« ${source.nom} » — ${source.niveau}`} isDark={isDark} onFermer={onFermer}>
      <div className="text-xs text-gray-500">
        {source.dateLabel}, {source.heureDebut}-{source.heureFin} · colonne {source.groupeLabel}
      </div>
      <div className="flex flex-col gap-2">
        <button onClick={onDeplacer} className={boutonPrincipal}>Déplacer vers un autre créneau</button>
        <button onClick={() => envoyer(false)} disabled={isLoading} className={boutonSecondaire(isDark)}>
          Retirer de {portee}
        </button>
        <button onClick={() => envoyer(true)} disabled={isLoading} className={boutonSecondaire(isDark)}>
          Retirer seulement de la colonne {source.groupeLabel}
        </button>
      </div>
      {erreur && <div className="text-xs text-red-700 dark:text-red-300">{erreur}</div>}
    </Cadre>
  );
};

// ─── Fenêtre : doublons de noms ─────────────────────────────────────────────

const ModaleDoublons: React.FC<{
  sessionId: number; niveau: string; pole: string; isDark: boolean; onFermer: () => void; onFait: (texte: string) => void;
}> = ({ sessionId, niveau, pole, isDark, onFermer, onFait }) => {
  const { data, isFetching, error } = useGetDoublonsMatieresQuery(sessionId);
  const [modifier, { isLoading }] = useModifierCorrespondanceMatieresMutation();
  const [erreur, setErreur] = useState('');
  const [voirIgnorees, setVoirIgnorees] = useState(false);

  const dansPortee = (x: { niveau: string; pole: string }) => (!niveau || x.niveau === niveau) && (!pole || x.pole === pole);
  const suggestions = (data?.suggestions || []).filter(dansPortee);
  const fusions = (data?.fusions || []).filter(dansPortee);
  const ignorees = (data?.ignorees || []).filter(dansPortee);

  const agir = async (action: 'fusionner' | 'separer' | 'ignorer' | 'retablir', x: { niveau: string; pole: string }, cles: string[], texte: string) => {
    setErreur('');
    try {
      await modifier({ sessionId, action, niveau: x.niveau, pole: x.pole, cles }).unwrap();
      onFait(texte);
    } catch (e: any) {
      setErreur(e?.data?.error || 'Action impossible.');
    }
  };

  const ligneSuggestion = (s: SuggestionDoublon, ignoree: boolean) => {
    const [a, b] = s.matieres;
    return (
      <div key={`${s.niveau}-${s.pole}-${a.cle}-${b.cle}`} className={`rounded border p-3 space-y-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={puce(isDark)}>{s.niveau}</span>
          <span className={puce(isDark)}>{libellePole(s.pole)}</span>
          <span className="font-medium">{CONFIANCE[s.confiance]}</span>
          <span className="text-gray-500">{s.raison}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          {[a, b].map((m) => (
            <div key={m.cle} className={`rounded px-2 py-1.5 ${isDark ? 'bg-gray-900/40' : 'bg-gray-50'}`}>
              <div className="font-medium">{m.noms.join(' / ')}</div>
              <div className="text-gray-500">{m.options.join(', ')}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {ignoree ? (
            <button
              disabled={isLoading}
              onClick={() => agir('retablir', s, [a.cle, b.cle], 'Suggestion rétablie.')}
              className={boutonSecondaire(isDark)}
            >Rétablir la suggestion</button>
          ) : (
            <>
              <button
                disabled={isLoading}
                onClick={() => agir('ignorer', s, [a.cle, b.cle], 'Suggestion écartée : ce sont deux matières différentes.')}
                className={boutonSecondaire(isDark)}
              >Deux matières différentes</button>
              <button
                disabled={isLoading}
                onClick={() => agir('fusionner', s, [a.cle, b.cle], `« ${a.noms[0]} » et « ${b.noms[0]} » se placent désormais ensemble.`)}
                className={boutonPrincipal}
              >Fusionner pour l'EDT</button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <Cadre
      titre={`Doublons de noms de matières${niveau ? ` — ${niveau}` : ''}${pole ? ` · ${libellePole(pole)}` : ''}`}
      isDark={isDark}
      onFermer={onFermer}
      large
    >
      <p className="text-xs text-gray-500">
        Une même matière écrite différemment d'une option à l'autre (accord, mot de liaison, numéro, faute de frappe) compte
        comme deux matières. Fusionner les regroupe pour l'emploi du temps seulement : aucune matière n'est renommée et chaque
        colonne garde son nom exact. Deux noms présents dans une même option ne sont jamais proposés.
      </p>
      {erreur && <div className="text-xs text-red-700 dark:text-red-300">{erreur}</div>}
      {isFetching && !data && <div className="flex justify-center py-6"><Loader2 className="animate-spin" size={20} /></div>}
      {error && !data && <div className="text-xs text-red-700">{(error as any)?.data?.error || 'Analyse impossible.'}</div>}

      {data && (
        <>
          <div className="font-medium text-xs">Suggestions ({suggestions.length})</div>
          {suggestions.length === 0 && <div className="text-xs text-gray-500">Aucun doublon probable détecté.</div>}
          <div className="space-y-2">{suggestions.map((s) => ligneSuggestion(s, false))}</div>

          {fusions.length > 0 && (
            <>
              <div className="font-medium text-xs pt-2">Fusions en place ({fusions.length})</div>
              <div className="space-y-2">
                {fusions.map((f) => (
                  <div key={`${f.niveau}-${f.pole}-${f.cle}`} className={`rounded border p-3 flex flex-wrap items-center justify-between gap-2 text-xs ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div>
                      <span className={puce(isDark)}>{f.niveau}</span>{' '}
                      <span className={puce(isDark)}>{libellePole(f.pole)}</span>{' '}
                      <span className="font-medium">{f.noms.join(' / ')}</span>
                      <div className="text-gray-500">{f.options.join(', ')}</div>
                    </div>
                    <button
                      disabled={isLoading}
                      onClick={() => agir('separer', f, [f.cle], `« ${f.noms.join(' / ')} » redeviennent des matières séparées.`)}
                      className={boutonSecondaire(isDark)}
                    >Séparer</button>
                  </div>
                ))}
              </div>
            </>
          )}

          {ignorees.length > 0 && (
            <div className="pt-2">
              <button onClick={() => setVoirIgnorees((v) => !v)} className="text-xs text-gray-500 underline">
                {voirIgnorees ? 'Masquer' : 'Voir'} les suggestions écartées ({ignorees.length})
              </button>
              {voirIgnorees && <div className="space-y-2 mt-2">{ignorees.map((s) => ligneSuggestion(s, true))}</div>}
            </div>
          )}
        </>
      )}
    </Cadre>
  );
};

// ─── Grille d'un niveau × pôle ──────────────────────────────────────────────

const GrilleManuelle: React.FC<{
  sessionId: number; niveau: string; pole: string; isDark: boolean;
  selection: GroupeMatiereAPlacer | null; deplacement: CreneauPose | null;
  onCaseVide: (c: Cible) => void; onCasePleine: (s: CreneauPose) => void; onDepot: (c: Cible, brut: string) => void;
}> = ({ sessionId, niveau, pole, isDark, selection, deplacement, onCaseVide, onCasePleine, onDepot }) => {
  const { data, isLoading, error } = useGetEmploiTempsGrilleManuelleQuery({ sessionId, niveau, pole });
  const [survol, setSurvol] = useState<string | null>(null);

  const cibles = useMemo(() => new Set(
    selection && selection.niveau === niveau ? selection.colonnes.filter((c) => !c.place).map((c) => c.groupe_id) : [],
  ), [selection, niveau]);

  const bordure = isDark ? 'border-gray-700' : 'border-gray-200';
  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="animate-spin" size={20} /></div>;
  }
  if (error || !data) {
    return <div className="text-sm text-gray-500 py-4">Grille indisponible pour {niveau} — {libellePole(pole)}.</div>;
  }

  return (
    <div className={`rounded-lg border ${bordure} ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
      <div className={`px-3 py-2 border-b ${bordure} text-sm font-semibold flex flex-wrap gap-x-3`}>
        <span>{niveau} — Pôle {libellePole(pole)}</span>
        <span className="font-normal text-gray-500">{data.semestre} · {data.periode}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border-collapse">
          <thead>
            <tr className={isDark ? 'bg-gray-900' : 'bg-gray-50'}>
              <th className={`border ${bordure} px-2 py-1.5 text-left`}>Jour</th>
              <th className={`border ${bordure} px-2 py-1.5 text-left`}>Horaire</th>
              {data.groupes.map((g) => (
                <th key={g.id} className={`border ${bordure} px-2 py-1.5 text-left min-w-[140px]`} title={g.option_noms.join(', ')}>
                  {g.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.jours.map((j) => j.creneaux.map((cr, idx) => {
              const heureDebut = cr.heure_debut || labelVersHeure(cr.heure_label.split('-')[0]);
              const heureFin = cr.heure_fin || labelVersHeure(cr.heure_label.split('-')[1]);
              const cible: Cible = { niveau, date: j.date, dateLabel: `${j.jour_semaine} ${j.date_label}`, heureDebut, heureFin };
              return (
                <tr key={`${j.date}-${cr.heure_label}`}>
                  {idx === 0 && (
                    <td rowSpan={j.creneaux.length} className={`border ${bordure} px-2 py-1 align-top font-medium whitespace-nowrap`}>
                      {j.jour_semaine}
                      <div className="font-normal text-gray-500">{j.date_label}</div>
                    </td>
                  )}
                  <td className={`border ${bordure} px-2 py-1 whitespace-nowrap text-gray-500`}>{cr.heure_label}</td>
                  {data.groupes.map((g) => {
                    const m = cr.matieres[g.id];
                    const idCase = `${j.date}|${heureDebut}|${g.id}`;
                    if (m) {
                      const source: CreneauPose = {
                        niveau, nom: m.matiere_nom, date: j.date, dateLabel: cible.dateLabel,
                        heureDebut, heureFin: labelVersHeure(m.heure_fin) || heureFin, groupeId: g.id, groupeLabel: g.label,
                      };
                      const enDeplacement = deplacement && deplacement.nom === m.matiere_nom
                        && deplacement.date === j.date && deplacement.heureDebut === heureDebut;
                      return (
                        <td
                          key={g.id}
                          draggable
                          onDragStart={(e) => e.dataTransfer.setData(DND, JSON.stringify({ type: 'deplacer', source }))}
                          onClick={() => onCasePleine(source)}
                          title="Cliquer ou glisser pour déplacer ; cliquer pour retirer"
                          className={`border ${bordure} px-2 py-1 align-top cursor-pointer ${isDark ? 'bg-blue-900/30 hover:bg-blue-900/50' : 'bg-blue-50 hover:bg-blue-100'} ${enDeplacement ? 'ring-2 ring-inset ring-amber-500' : ''} ${m.est_inactif ? 'opacity-50' : ''}`}
                        >
                          <div className="font-medium leading-tight">{m.matiere_nom}</div>
                          {m.duree_label && <div className="text-[10px] text-gray-500">{m.duree_label}</div>}
                        </td>
                      );
                    }
                    const estCible = cibles.has(g.id);
                    return (
                      <td
                        key={g.id}
                        onClick={() => onCaseVide(cible)}
                        onDragOver={(e) => {
                          if (Array.from(e.dataTransfer.types).includes(DND)) {
                            e.preventDefault();
                            setSurvol(idCase);
                          }
                        }}
                        onDragLeave={() => setSurvol((s) => (s === idCase ? null : s))}
                        onDrop={(e) => {
                          e.preventDefault();
                          setSurvol(null);
                          onDepot(cible, e.dataTransfer.getData(DND));
                        }}
                        className={`border ${bordure} h-9 ${
                          survol === idCase
                            ? (isDark ? 'bg-blue-800/60' : 'bg-blue-100')
                            : estCible
                              ? (isDark ? 'bg-emerald-900/30 cursor-pointer' : 'bg-emerald-50 cursor-pointer')
                              : deplacement && deplacement.niveau === niveau ? 'cursor-pointer' : ''
                        }`}
                      />
                    );
                  })}
                </tr>
              );
            }))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Page de conception ─────────────────────────────────────────────────────

const ConceptionManuelle: React.FC<Props> = ({ session, isDark }) => {
  const [niveauFiltre, setNiveauFiltre] = useState('');
  const [poleFiltre, setPoleFiltre] = useState('');
  const { data, isLoading, isFetching, error } = useGetMatieresAPlacerQuery({ sessionId: session.id, pole: poleFiltre || undefined });
  const { data: combos = [] } = useGetNiveauxDisponiblesQuery(session.id);
  const { data: doublons } = useGetDoublonsMatieresQuery(session.id);
  const [preparer, { isLoading: preparation }] = usePreparerGrilleMutation();
  const [recherche, setRecherche] = useState('');
  const [selection, setSelection] = useState<{ niveau: string; cle: string } | null>(null);
  const [deplacement, setDeplacement] = useState<CreneauPose | null>(null);
  const [modale, setModale] = useState<Modale>(null);
  const [voirPlacees, setVoirPlacees] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; texte: string } | null>(null);

  const pole = poleFiltre || undefined;
  const niveaux = useMemo(() => data?.niveaux ?? [], [data]);
  const trouverItem = (niveau: string, cle: string) =>
    niveaux.find((n) => n.niveau === niveau)?.a_placer.find((i) => i.cle === cle) ?? null;
  const itemSelectionne = selection ? trouverItem(selection.niveau, selection.cle) : null;

  const polesDisponibles = useMemo(
    () => Array.from(new Set(combos.map((c) => c.pole))).sort((a, b) => libellePole(a).localeCompare(libellePole(b))),
    [combos],
  );
  const nbDoublons = (doublons?.suggestions || []).filter(
    (s) => (!niveauFiltre || s.niveau === niveauFiltre) && (!poleFiltre || s.pole === poleFiltre),
  ).length;

  const q = recherche.trim().toLowerCase();
  const correspond = (i: GroupeMatiereAPlacer) =>
    !q || i.nom.toLowerCase().includes(q) || i.variantes.some((v) => v.toLowerCase().includes(q))
    || i.colonnes.some((c) => c.label.toLowerCase().includes(q));
  const niveauxVisibles = niveaux.filter((n) => !niveauFiltre || n.niveau === niveauFiltre);
  const aPlacer = niveauxVisibles.flatMap((n) => n.a_placer.filter(correspond));
  const placees = niveauxVisibles.flatMap((n) => n.placees.filter(correspond));
  const horsProgramme = niveauxVisibles.flatMap((n) => n.hors_programme);
  const grilles = combos
    .filter((c) => (!niveauFiltre || c.niveau === niveauFiltre) && (!poleFiltre || c.pole === poleFiltre))
    .sort((a, b) => (a.niveau + a.pole).localeCompare(b.niveau + b.pole));

  const changerPole = (p: string) => {
    setPoleFiltre(p);
    setSelection(null);
    setDeplacement(null);
  };

  const lancerPreparation = async () => {
    setMessage(null);
    try {
      const r = await preparer(session.id).unwrap();
      setMessage({
        ok: true,
        texte: r.groupes_crees.length
          ? `${r.groupes_crees.length} colonne(s) ajoutée(s) à la grille.`
          : 'Les colonnes de la grille étaient déjà à jour.',
      });
    } catch (e: any) {
      setMessage({ ok: false, texte: e?.data?.error || 'Préparation de la grille impossible.' });
    }
  };

  const surCaseVide = (cible: Cible) => {
    if (deplacement) {
      if (deplacement.niveau !== cible.niveau) {
        setMessage({ ok: false, texte: 'Une matière se déplace dans la grille de son niveau.' });
        return;
      }
      setModale({ type: 'deplacer', source: deplacement, cible });
      return;
    }
    if (itemSelectionne && itemSelectionne.niveau === cible.niveau) {
      setModale({ type: 'placer', item: itemSelectionne, cible });
    }
  };

  const surDepot = (cible: Cible, brut: string) => {
    let charge: any;
    try { charge = JSON.parse(brut); } catch { return; }
    if (charge?.type === 'placer') {
      const item = trouverItem(charge.niveau, charge.cle);
      if (!item) return;
      if (item.niveau !== cible.niveau) {
        setMessage({ ok: false, texte: `« ${item.nom} » est une matière de ${item.niveau} : déposez-la dans une grille ${item.niveau}.` });
        return;
      }
      setModale({ type: 'placer', item, cible });
    } else if (charge?.type === 'deplacer' && charge.source) {
      if (charge.source.niveau !== cible.niveau) {
        setMessage({ ok: false, texte: 'Une matière se déplace dans la grille de son niveau.' });
        return;
      }
      setModale({ type: 'deplacer', source: charge.source as CreneauPose, cible });
    }
  };

  const terminer = (texte: string) => {
    setModale(null);
    setDeplacement(null);
    setMessage({ ok: true, texte });
  };

  const carte = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const ongletClasses = (actif: boolean) => `px-2 py-1 rounded text-xs border ${
    actif ? 'bg-blue-600 border-blue-600 text-white' : isDark ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'
  }`;

  if (isLoading) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={28} /></div>;
  }
  if (error || !data) {
    return <div className={`rounded-lg border p-6 text-sm ${carte}`}>{(error as any)?.data?.error || 'Chargement de la conception impossible.'}</div>;
  }

  if (data.nb_groupes_options === 0) {
    return (
      <div className={`rounded-lg border p-6 space-y-3 ${carte}`}>
        <h2 className="font-semibold">Conception manuelle — grille à préparer</h2>
        <p className="text-sm text-gray-500">
          La grille reprend les jours d'examen de la session et ses horaires. Chaque colonne regroupe les options qui
          ont exactement les mêmes matières. Aucune matière n'est placée : vous les insérez ensuite une à une.
        </p>
        <button onClick={lancerPreparation} disabled={preparation} className={boutonPrincipal}>
          {preparation ? 'Préparation…' : 'Préparer la grille'}
        </button>
        {message && <div className={`text-sm ${message.ok ? 'text-green-700' : 'text-red-700'}`}>{message.texte}</div>}
      </div>
    );
  }

  const nbTotal = niveauxVisibles.reduce((s, n) => s + n.total, 0);
  const nbCompletes = niveauxVisibles.reduce((s, n) => s + n.nb_completes, 0);

  return (
    <div className="space-y-3">
      {message && (
        <div className={`flex items-start justify-between gap-2 rounded border px-3 py-2 text-sm ${
          message.ok
            ? 'border-green-300 bg-green-50 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300'
            : 'border-red-300 bg-red-50 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'
        }`}>
          <span>{message.texte}</span>
          <button onClick={() => setMessage(null)}><X size={14} /></button>
        </div>
      )}
      {deplacement && (
        <div className="flex items-center justify-between gap-2 rounded border border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200 px-3 py-2 text-sm">
          <span>
            Déplacement de « {deplacement.nom} » ({deplacement.niveau}, {deplacement.dateLabel} {deplacement.heureDebut}) :
            cliquez sur une case vide de destination.
          </span>
          <button onClick={() => setDeplacement(null)} className={boutonSecondaire(isDark)}>Annuler</button>
        </div>
      )}
      {data.colonnes_manquantes && (
        <div className="flex items-center justify-between gap-2 rounded border border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200 px-3 py-2 text-sm">
          <span>Des options n'ont pas encore de colonne dans la grille (nouvelle matière ou option).</span>
          <button onClick={lancerPreparation} disabled={preparation} className={boutonPrincipal}>
            {preparation ? 'Mise à jour…' : 'Mettre à jour les colonnes'}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[340px_minmax(0,1fr)] gap-4 items-start">
        {/* Matières à placer */}
        <div className={`rounded-lg border ${carte} xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] flex flex-col`}>
          <div className={`p-3 border-b space-y-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-baseline justify-between">
              <h3 className="font-semibold text-sm">Matières à placer</h3>
              <span className="text-xs text-gray-500">
                {isFetching && <Loader2 size={11} className="inline animate-spin mr-1" />}
                {nbCompletes}/{nbTotal} placées
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {['', ...NIVEAUX].map((n) => {
                const nb = n ? (niveaux.find((x) => x.niveau === n)?.a_placer.length ?? 0) : data.total_a_placer;
                return (
                  <button key={n || 'tous'} onClick={() => setNiveauFiltre(n)} className={ongletClasses(niveauFiltre === n)}>
                    {n || 'Tous niveaux'} <span className="opacity-70">{nb}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-1">
              {['', ...polesDisponibles].map((p) => (
                <button key={p || 'tous'} onClick={() => changerPole(p)} className={ongletClasses(poleFiltre === p)}>
                  {p ? libellePole(p) : 'Tous pôles'}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-gray-500">
              {poleFiltre
                ? `Conception par niveau et pôle : seules les colonnes ${libellePole(poleFiltre)} sont concernées.`
                : 'Conception par niveau : une matière commune à plusieurs pôles se place en une fois.'}
            </div>
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="Matière ou colonne…"
                className={`w-full pl-7 ${classesChamp(isDark)}`}
              />
            </div>
            <button
              onClick={() => setModale({ type: 'doublons' })}
              className={`w-full text-left text-xs px-2 py-1.5 rounded border ${
                nbDoublons > 0
                  ? 'border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200'
                  : isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'
              }`}
            >
              {nbDoublons > 0
                ? `${nbDoublons} doublon${nbDoublons > 1 ? 's' : ''} de noms probable${nbDoublons > 1 ? 's' : ''} (Mathématique / Mathématiques…) : vérifier`
                : 'Doublons de noms : aucun détecté (voir les fusions)'}
            </button>
          </div>

          <div className="overflow-y-auto p-2 space-y-1.5">
            {aPlacer.length === 0 && (
              <div className="text-xs text-gray-500 p-2">
                {q ? 'Aucune matière ne correspond à la recherche.' : 'Toutes les matières sont placées.'}
              </div>
            )}
            {aPlacer.map((i) => {
              const actif = selection?.cle === i.cle && selection?.niveau === i.niveau;
              return (
                <div
                  key={`${i.niveau}-${i.cle}`}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData(DND, JSON.stringify({ type: 'placer', niveau: i.niveau, cle: i.cle }))}
                  onClick={() => { setDeplacement(null); setSelection(actif ? null : { niveau: i.niveau, cle: i.cle }); }}
                  className={`rounded border px-2 py-1.5 cursor-pointer text-xs ${
                    actif
                      ? 'border-blue-500 ring-1 ring-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : isDark ? 'border-gray-700 hover:border-gray-500' : 'border-gray-200 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-medium leading-tight">{i.nom}</span>
                    <span className={`shrink-0 ${puce(isDark)}`}>{i.niveau}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1 mt-1">
                    {i.poles.map((p) => <span key={p} className={puce(isDark)}>{libellePole(p)}</span>)}
                    <span className="text-gray-500">
                      {i.nb_options} option{i.nb_options > 1 ? 's' : ''}
                      {i.nb_placees > 0 && <span className="text-amber-700 dark:text-amber-300"> · {i.nb_placees}/{i.nb_colonnes} colonnes placées</span>}
                    </span>
                  </div>
                  {i.variantes.length > 0 && (
                    <div className="text-gray-500 mt-0.5">Regroupe : {i.variantes.join(' / ')}</div>
                  )}
                  {actif && (
                    <div className="mt-1 space-y-0.5">
                      {i.colonnes.map((c) => (
                        <div key={c.groupe_id} className={c.place ? 'text-gray-400' : ''}>
                          {c.label}{!pole && ` (${libellePole(c.pole_key)})`}
                          {c.place ? ` — ${dateCourte(c.date)} ${c.heure_debut}` : ' — à placer'}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {placees.length > 0 && (
              <div className="pt-2">
                <button onClick={() => setVoirPlacees((v) => !v)} className="text-xs text-gray-500 underline">
                  {voirPlacees ? 'Masquer' : 'Voir'} les matières déjà placées ({placees.length})
                </button>
                {voirPlacees && (
                  <div className="mt-1 space-y-1">
                    {placees.map((i) => (
                      <div key={`${i.niveau}-${i.cle}`} className={`rounded border px-2 py-1 text-xs ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <span className="font-medium">{i.nom}</span> <span className="text-gray-500">({i.niveau} · {i.poles.map(libellePole).join(', ')})</span>
                        <div className="text-gray-500">
                          {Array.from(new Set(i.colonnes.map((c) => `${dateCourte(c.date)} ${c.heure_debut}`))).join(' · ')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {horsProgramme.length > 0 && (
              <div className="pt-2 text-xs text-amber-700 dark:text-amber-300">
                <div className="font-medium">Placées mais hors programme (matière désactivée, TP ou renommée) :</div>
                {horsProgramme.map((h) => <div key={`${h.niveau}-${h.nom}`}>{h.nom} ({h.niveau}, {h.nb_creneaux} créneau(x))</div>)}
              </div>
            )}
          </div>
        </div>

        {/* Grilles */}
        <div className="space-y-4 min-w-0">
          {grilles.length === 0 && (
            <div className={`rounded-lg border p-6 text-sm text-gray-500 ${carte}`}>Aucune grille pour cette sélection.</div>
          )}
          {grilles.map((c) => (
            <GrilleManuelle
              key={`${c.niveau}-${c.pole}`}
              sessionId={session.id}
              niveau={c.niveau}
              pole={c.pole}
              isDark={isDark}
              selection={itemSelectionne}
              deplacement={deplacement}
              onCaseVide={surCaseVide}
              onCasePleine={(source) => {
                if (deplacement) return;
                setModale({ type: 'actions', source });
              }}
              onDepot={surDepot}
            />
          ))}
        </div>
      </div>

      {modale?.type === 'placer' && (
        <ModalePlacer sessionId={session.id} pole={pole} item={modale.item} cible={modale.cible} isDark={isDark} onFermer={() => setModale(null)} onFait={terminer} />
      )}
      {modale?.type === 'deplacer' && (
        <ModaleDeplacer sessionId={session.id} pole={pole} source={modale.source} cible={modale.cible} isDark={isDark} onFermer={() => setModale(null)} onFait={terminer} />
      )}
      {modale?.type === 'actions' && (
        <ModaleActions
          sessionId={session.id}
          pole={pole}
          source={modale.source}
          isDark={isDark}
          onFermer={() => setModale(null)}
          onDeplacer={() => { setDeplacement(modale.source); setSelection(null); setModale(null); }}
          onFait={terminer}
        />
      )}
      {modale?.type === 'doublons' && (
        <ModaleDoublons
          sessionId={session.id}
          niveau={niveauFiltre}
          pole={poleFiltre}
          isDark={isDark}
          onFermer={() => setModale(null)}
          onFait={(texte) => setMessage({ ok: true, texte })}
        />
      )}
    </div>
  );
};

export default ConceptionManuelle;
